/**
 * Credentials kept for a site: the ways into what it depends on. Only the
 * owner and administrators (`secrets.manage`) see them; the password is sealed
 * and shown on request, each showing in the audit trail without its value.
 */
export const CREDENTIAL_KINDS = {
  cms: 'CMS',
  hosting: 'Хостинг',
  ssh: 'SSH',
  sftp: 'SFTP / FTP',
  database: 'База данных',
  email: 'Почта',
  registrar: 'Регистратор',
  other: 'Другое',
} as const;

export type CredentialKind = keyof typeof CREDENTIAL_KINDS;

export const CREDENTIAL_KIND_NAMES = Object.keys(CREDENTIAL_KINDS) as [CredentialKind, ...CredentialKind[]];

/**
 * What is kept for a server (ADR-0058): the ways a person gets into the
 * machine. For people only — the panel reaches a server through its agent.
 */
export const NODE_CREDENTIAL_KIND_NAMES = ['ssh', 'sftp', 'hosting', 'database', 'other'] as const satisfies readonly CredentialKind[];

/** A credential that can sign into the site's CMS from the panel: a CMS login with its password kept. */
export function opensCms(credential: { readonly kind: string; readonly login: string | null; readonly hasPassword: boolean }): boolean {
  return credential.kind === 'cms' && Boolean(credential.login) && credential.hasPassword;
}

/**
 * How the panel signs in with a CMS login where the CMS is not WordPress
 * (ADR-0042): the sign-in form's address and the names of its fields, as the
 * page's own HTML has them — any CMS whose sign-in form takes a plain post.
 */
export interface SignInFormSpec {
  /** Where the form is sent: the `action` of the page's `<form>`, as a full address. */
  readonly action: string;
  readonly userField: string;
  readonly passwordField: string;
  /** Fields the form always carries with the same value: `remember=1`, `task=login`. */
  readonly extra: Readonly<Record<string, string>>;
}

/**
 * Whether a sign-in form may be sent where it says (ADR-0042): to one of the
 * site's own names, with or without www, to its admin's host, or to the host
 * the credential itself names — a CMS console may live apart from the site.
 * Anywhere else a password has no business going.
 */
export function signInHostAllowed(
  action: string,
  place: { readonly domains: readonly string[]; readonly adminUrl: string | null; readonly credentialUrl: string | null },
): boolean {
  const host = hostOf(action);
  if (!host) return false;
  const bare = (name: string) => name.toLowerCase().replace(/^www\./, '');
  const allowed = new Set(
    [...place.domains, hostOf(place.adminUrl), hostOf(place.credentialUrl)].filter((name): name is string => Boolean(name)).map(bare),
  );
  return allowed.has(bare(host));
}

function hostOf(address: string | null): string | null {
  if (!address) return null;
  try {
    const url = new URL(address);
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.hostname.toLowerCase() : null;
  } catch {
    return null;
  }
}

/**
 * Whether the panel signs in with this login at a click: on a WordPress site,
 * or with the form an operator described for any other CMS.
 */
export function signsInWith(
  site: { readonly engine: string; readonly platform: string | null },
  credential: { readonly kind: string; readonly login: string | null; readonly hasPassword: boolean; readonly signInForm: SignInFormSpec | null },
): boolean {
  return opensCms(credential) && (credential.signInForm !== null || site.platform === 'wordpress' || site.engine === 'wordpress');
}

export const CREDENTIAL_LIMITS = {
  label: 120,
  url: 500,
  login: 200,
  password: 1000,
  notes: 2000,
} as const;
